import { INITIAL_UI_BACKGROUND, type ViewMode } from '../core/mdlConstants'

export type Vector3Tuple = [number, number, number]

export type PlaybackUrlPreset = {
  sequenceIndex?: number
  frame?: number
  isPaused?: boolean
  playbackRate?: number
  visibleSubModelIndices?: number[]
}

export type CameraUrlPreset = {
  position?: Vector3Tuple
  target?: Vector3Tuple
  angles?: Vector3Tuple
}

export type UrlConfig = {
  modelQuery?: string
  modelUrl?: string
  viewMode?: ViewMode
  isEmbed: boolean
  showOptionsMenu: boolean
  backgroundColor?: string
  playbackPreset: PlaybackUrlPreset
  cameraPreset: CameraUrlPreset
}

export type ShareableUrlConfig = {
  modelUrl: string
  viewMode: ViewMode
  backgroundColor: string
  playbackPreset?: PlaybackUrlPreset
  cameraPreset?: CameraUrlPreset
}

const TRUE_VALUES = new Set(['true', '1', 'yes', 'on'])
const FALSE_VALUES = new Set(['false', '0', 'no', 'off'])
const MODEL_QUERY_NAMES = ['model', 'mdl', 'url']
const VIEW_MODE_QUERY_NAMES = ['viewtype', 'viewType', 'view']
const BACKGROUND_QUERY_NAMES = ['background', 'backgroundColor', 'bg']
const SEQUENCE_QUERY_NAMES = ['sequence', 'seq']
const FRAME_QUERY_NAMES = ['frame']
const PAUSE_QUERY_NAMES = ['pause', 'paused', 'autoplay', 'play']
const PLAYBACK_RATE_QUERY_NAMES = ['rate', 'speed', 'playbackrate', 'playbackRate']
const BODY_PART_QUERY_NAMES = ['bodyparts', 'bodypart', 'submodels', 'submodel']
const POSITION_QUERY_NAMES = ['position', 'pos', 'origin']
const TARGET_QUERY_NAMES = ['target', 'lookat', 'lookAt']
const ANGLES_QUERY_NAMES = ['angles', 'angle', 'rotation']
const SHARE_QUERY_NAMES = new Set(
  [
    ...MODEL_QUERY_NAMES,
    ...VIEW_MODE_QUERY_NAMES,
    ...BACKGROUND_QUERY_NAMES,
    ...SEQUENCE_QUERY_NAMES,
    ...FRAME_QUERY_NAMES,
    ...PAUSE_QUERY_NAMES,
    ...PLAYBACK_RATE_QUERY_NAMES,
    ...BODY_PART_QUERY_NAMES,
    ...POSITION_QUERY_NAMES,
    ...TARGET_QUERY_NAMES,
    ...ANGLES_QUERY_NAMES
  ].map(name => name.toLowerCase())
)

const getFirstParam = (params: URLSearchParams, names: string[]) => {
  const normalizedNames = new Set(names.map(name => name.toLowerCase()))

  for (const [key, value] of params) {
    if (normalizedNames.has(key.toLowerCase()) && value.trim() !== '') {
      return value.trim()
    }
  }

  return undefined
}

const isBodyPartIndexParam = (key: string) => /^(?:bodypart|part|submodel)\d+$/i.test(key)

const deleteModelSearchParams = (params: URLSearchParams) => {
  const modelQueryNames = new Set(MODEL_QUERY_NAMES.map(name => name.toLowerCase()))

  for (const key of Array.from(params.keys())) {
    if (modelQueryNames.has(key.toLowerCase())) {
      params.delete(key)
    }
  }
}

const deleteShareSearchParams = (params: URLSearchParams) => {
  for (const key of Array.from(params.keys())) {
    if (SHARE_QUERY_NAMES.has(key.toLowerCase()) || isBodyPartIndexParam(key)) {
      params.delete(key)
    }
  }
}

const normalizeColor = (value: string) => {
  const normalizedValue = value.trim().startsWith('#') ? value.trim() : `#${value.trim()}`

  return /^#[0-9a-f]{6}$/i.test(normalizedValue)
    ? normalizedValue.toLowerCase()
    : undefined
}

const formatUrlNumber = (value: number, fractionDigits = 3) => {
  const roundedValue = Number(value.toFixed(fractionDigits))
  const normalizedValue = Object.is(roundedValue, -0) ? 0 : roundedValue

  return Number.isInteger(normalizedValue)
    ? String(normalizedValue)
    : normalizedValue.toFixed(fractionDigits).replace(/\.?0+$/, '')
}

const formatVectorParam = (vector: Vector3Tuple) => vector.map(value => formatUrlNumber(value)).join(',')

const hasCustomBodyParts = (subModelIndices: number[]) => subModelIndices.some(subModelIndex => subModelIndex > 0)

const formatReadableUrl = (url: URL) => url.toString().replace(/%2C/gi, ',')

const setPlaybackParams = (params: URLSearchParams, playbackPreset?: PlaybackUrlPreset) => {
  if (!playbackPreset) {
    return
  }

  if (typeof playbackPreset.sequenceIndex === 'number' && playbackPreset.sequenceIndex > 0) {
    params.set('sequence', String(Math.trunc(playbackPreset.sequenceIndex)))
  }

  if (typeof playbackPreset.frame === 'number' && playbackPreset.frame > 0) {
    params.set('frame', String(Math.trunc(playbackPreset.frame)))
  }

  if (playbackPreset.isPaused) {
    params.set('paused', '1')
  }

  if (typeof playbackPreset.playbackRate === 'number' && playbackPreset.playbackRate !== 1) {
    params.set('rate', formatUrlNumber(playbackPreset.playbackRate, 2))
  }

  if (
    playbackPreset.visibleSubModelIndices
    && hasCustomBodyParts(playbackPreset.visibleSubModelIndices)
  ) {
    params.set(
      'bodyparts',
      playbackPreset.visibleSubModelIndices.map(subModelIndex => String(Math.trunc(subModelIndex))).join(',')
    )
  }
}

const setCameraParams = (params: URLSearchParams, cameraPreset?: CameraUrlPreset) => {
  if (cameraPreset?.position) {
    params.set('position', formatVectorParam(cameraPreset.position))
  }

  if (cameraPreset?.angles) {
    params.set('angles', formatVectorParam(cameraPreset.angles))
  }

  if (cameraPreset?.target) {
    params.set('target', formatVectorParam(cameraPreset.target))
  }
}

export const createShareableUrl = (
  config: ShareableUrlConfig,
  baseUrl = window.location.href
) => {
  const url = new URL(baseUrl)
  const backgroundColor = normalizeColor(config.backgroundColor)
  const initialBackgroundColor = normalizeColor(INITIAL_UI_BACKGROUND)

  deleteShareSearchParams(url.searchParams)
  url.searchParams.set('model', config.modelUrl)

  if (config.viewMode !== 'free-view') {
    url.searchParams.set('viewtype', 'weapon')
  }

  if (backgroundColor && backgroundColor !== initialBackgroundColor) {
    url.searchParams.set('background', backgroundColor.slice(1))
  }

  setPlaybackParams(url.searchParams, config.playbackPreset)
  setCameraParams(url.searchParams, config.cameraPreset)

  return formatReadableUrl(url)
}

export const createEmbedUrl = (baseUrl: string) => {
  const url = new URL(baseUrl)

  url.searchParams.set('embed', '1')

  return formatReadableUrl(url)
}

export const createUrlWithoutShareParams = (baseUrl = window.location.href) => {
  const url = new URL(baseUrl)

  deleteShareSearchParams(url.searchParams)

  return formatReadableUrl(url)
}

export const createHomeUrl = (baseUrl = window.location.href) => {
  const url = new URL(baseUrl)

  url.search = ''

  return formatReadableUrl(url)
}

export const createUrlWithModelQuery = (modelValue: string, baseUrl = window.location.href) => {
  const url = new URL(baseUrl)

  deleteModelSearchParams(url.searchParams)
  url.searchParams.set('model', modelValue)

  return formatReadableUrl(url)
}

export const parseModelUrl = (value: string, baseUrl: string) => {
  try {
    const url = new URL(value.trim(), baseUrl)

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return undefined
    }

    if (!url.pathname.toLowerCase().endsWith('.mdl')) {
      return undefined
    }

    return url.href
  } catch {
    return undefined
  }
}

const parseBooleanParam = (params: URLSearchParams, names: string[]) => {
  const rawValue = getFirstParam(params, names)

  if (!rawValue) {
    return undefined
  }

  const normalizedValue = rawValue.toLowerCase()

  if (TRUE_VALUES.has(normalizedValue)) {
    return true
  }

  if (FALSE_VALUES.has(normalizedValue)) {
    return false
  }

  return undefined
}

const parseIntegerParam = (params: URLSearchParams, names: string[]) => {
  const rawValue = getFirstParam(params, names)

  if (!rawValue || !/^\d+$/.test(rawValue)) {
    return undefined
  }

  const parsedValue = Number(rawValue)

  return Number.isSafeInteger(parsedValue) ? parsedValue : undefined
}

const parseNumberParam = (params: URLSearchParams, names: string[]) => {
  const rawValue = getFirstParam(params, names)

  if (!rawValue) {
    return undefined
  }

  const parsedValue = Number(rawValue)

  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : undefined
}

const parseVectorParam = (params: URLSearchParams, names: string[]): Vector3Tuple | undefined => {
  const rawValue = getFirstParam(params, names)

  if (!rawValue) {
    return undefined
  }

  const values = rawValue
    .split(/[\s,;:|]+/)
    .map(item => item.trim())
    .filter(Boolean)

  if (values.length !== 3) {
    return undefined
  }

  const vector = values.map(Number)

  return vector.every(Number.isFinite)
    ? vector as Vector3Tuple
    : undefined
}

const parseColorParam = (params: URLSearchParams) => {
  const rawValue = getFirstParam(params, ['background', 'backgroundColor', 'bg'])

  if (!rawValue) {
    return undefined
  }

  const color = rawValue.startsWith('#') ? rawValue : `#${rawValue}`

  if (/^#[0-9a-f]{6}$/i.test(color)) {
    return color.toLowerCase()
  }

  const shortHexMatch = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(color)

  if (shortHexMatch) {
    return `#${shortHexMatch[1]}${shortHexMatch[1]}${shortHexMatch[2]}${shortHexMatch[2]}${shortHexMatch[3]}${shortHexMatch[3]}`.toLowerCase()
  }

  return undefined
}

const parseViewModeParam = (params: URLSearchParams): ViewMode | undefined => {
  const rawValue = getFirstParam(params, ['viewtype', 'viewType', 'view'])

  if (!rawValue) {
    return undefined
  }

  const normalizedValue = rawValue.toLowerCase().replace(/_/g, '-')

  if (normalizedValue === 'free' || normalizedValue === 'free-view' || normalizedValue === 'freeview') {
    return 'free-view'
  }

  if (normalizedValue === 'weapon' || normalizedValue === 'weapon-origin' || normalizedValue === 'weaponorigin') {
    return 'weapon-origin'
  }

  return undefined
}

const parseIntegerList = (value?: string) => {
  if (!value) {
    return undefined
  }

  const values = value.split(',').map(item => item.trim())

  if (values.length === 0) {
    return undefined
  }

  const parsedValues = values.map(item => (/^\d+$/.test(item) ? Number(item) : undefined))

  if (parsedValues.every(item => typeof item !== 'number' || Number.isSafeInteger(item))) {
    return parsedValues as Array<number | undefined>
  }

  return undefined
}

const parseBodyPartPreset = (params: URLSearchParams) => {
  const visibleSubModelIndices = parseIntegerList(getFirstParam(params, ['bodyparts', 'bodypart', 'submodels', 'submodel']))
    ?? []

  for (const [key, value] of params) {
    const match = /^(?:bodypart|part|submodel)(\d+)$/i.exec(key)

    if (!match || !/^\d+$/.test(value.trim())) {
      continue
    }

    const bodyPartIndex = Number(match[1])
    const subModelIndex = Number(value.trim())

    if (Number.isSafeInteger(bodyPartIndex) && Number.isSafeInteger(subModelIndex)) {
      visibleSubModelIndices[bodyPartIndex] = subModelIndex
    }
  }

  return visibleSubModelIndices.some(item => typeof item === 'number')
    ? visibleSubModelIndices as number[]
    : undefined
}

const parsePlaybackPreset = (params: URLSearchParams): PlaybackUrlPreset => {
  const pause = parseBooleanParam(params, ['pause', 'paused'])
  const autoplay = parseBooleanParam(params, ['autoplay', 'play'])
  const playbackRate = parseNumberParam(params, ['rate', 'speed', 'playbackrate', 'playbackRate'])

  return {
    sequenceIndex:          parseIntegerParam(params, ['sequence', 'seq']),
    frame:                  parseIntegerParam(params, ['frame']),
    isPaused:               pause ?? (typeof autoplay === 'boolean' ? !autoplay : undefined),
    playbackRate:           playbackRate,
    visibleSubModelIndices: parseBodyPartPreset(params)
  }
}

const parseCameraPreset = (params: URLSearchParams): CameraUrlPreset => ({
  position: parseVectorParam(params, ['position', 'pos', 'origin']),
  target:   parseVectorParam(params, ['target', 'lookat', 'lookAt']),
  angles:   parseVectorParam(params, ['angles', 'angle', 'rotation'])
})

export const parseUrlConfig = (
  search = window.location.search,
  baseUrl = window.location.href
): UrlConfig => {
  const normalizedSearch = search.startsWith('?') || search === '' ? search : `?${search}`
  const params = new URLSearchParams(normalizedSearch)
  const modelUrl = getFirstParam(params, ['model', 'mdl', 'url'])
  const embed = parseBooleanParam(params, ['embed'])
  const menu = parseBooleanParam(params, ['menu', 'controls'])

  return {
    modelQuery:        modelUrl,
    modelUrl:          modelUrl ? parseModelUrl(modelUrl, baseUrl) : undefined,
    viewMode:          parseViewModeParam(params),
    isEmbed:           embed === true,
    showOptionsMenu:   menu ?? (typeof embed === 'boolean' ? !embed : true),
    backgroundColor:   parseColorParam(params),
    playbackPreset:    parsePlaybackPreset(params),
    cameraPreset:      parseCameraPreset(params)
  }
}
